import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * One selectable row or card in a Data-step picker (CT-5191 / CT-5194).
 *
 * `SelectableListItem` and `Card` from the DS are **visual only** — their
 * checkbox/radio carries `pointer-events-none` and the DS docblock expects an
 * interactive parent to own the click, the role and the keyboard. This is that
 * parent, deliberately typed `role="radio"` rather than reusing `CartContainer`
 * (`role="button"`), because these pickers are single-choice groups and the
 * selection has to be announced as such.
 *
 * Wrap a set of these in an element with `role="radiogroup"`; `TabNav`-style
 * arrow navigation comes from `PickerOptionGroup`.
 *
 * **Children that contain their own ARIA role must be `aria-hidden`.** The DS
 * `SelectableListItem` renders a `RadioButton`, which is itself `role="radio"`;
 * left visible to assistive tech that produces two nested radios per row and
 * breaks the group. Hide the visual composite and pass a complete `ariaLabel`
 * instead — that is what the callers here do.
 */
const PickerOption = memo(function PickerOption({
  value,
  selected,
  disabled,
  onSelect,
  ariaLabel,
  className = '',
  children,
}) {
  const handleClick = useCallback(() => {
    if (!disabled) onSelect(value);
  }, [disabled, onSelect, value]);

  const handleKeyDown = useCallback(
    (event) => {
      if (disabled) return;
      // Space and Enter both select: `role="radio"` is expected to respond to
      // Space, and a div does not do it for us.
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        onSelect(value);
      }
    },
    [disabled, onSelect, value],
  );

  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={[
        'min-w-0 cursor-pointer rounded-lg border bg-surface transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        selected ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/30' : 'border-edge hover:border-brand-300',
        disabled ? 'cursor-not-allowed opacity-50' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
});

PickerOption.propTypes = {
  value: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default PickerOption;
